/*
 * Recovery for failed lazy-chunk loads after a deployment.
 *
 * When a new build ships, the hashed chunk names change. A browser still
 * running the previous build (old index.html in memory) will 404 when it
 * lazy-loads a chunk whose name no longer exists, surfacing as
 * "Failed to fetch dynamically imported module".
 *
 * These failures arrive through two different paths:
 *  - window 'error' / 'unhandledrejection' (wired in main.ts), and
 *  - Angular's Zone-based ErrorHandler, for dynamic import() rejections that
 *    happen inside the Angular zone (e.g. a lazy import in a component's
 *    ngOnInit, or a lazy route). Those never reach the window handlers.
 *
 * This module is the single source of truth for both paths: detection plus a
 * loop-guarded hard reload that cache-busts to pull the fresh index.html.
 * Keeping one implementation matters — three ad-hoc copies with slightly
 * different message matching is how a browser-specific wording (Safari's MIME
 * error) ends up unhandled on one path and handled on another.
 */

const RELOAD_KEY = 'chunk_error_reloads';
const RELOAD_WINDOW_MS = 30000;
const MAX_RELOADS = 3;

/**
 * True when the given error/reason is a chunk (lazy module) load failure.
 * Accepts the various shapes these errors take across browsers and the two
 * delivery paths (Error object, ErrorEvent message, plain rejection reason).
 *
 * "is not a valid JavaScript MIME type" covers the case where the server
 * answers a missing chunk with index.html (HTTP 200, text/html) instead of a
 * 404 — Safari then reports a MIME error rather than a fetch failure.
 */
export function isChunkLoadError(reason: unknown): boolean {
  const candidate = reason as { name?: unknown; message?: unknown } | null | undefined;
  const name = typeof candidate?.name === 'string' ? candidate.name : '';
  const message = typeof candidate?.message === 'string' ? candidate.message : '';

  return (
    name === 'ChunkLoadError' ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Failed to load module script') ||
    // Safari's wording for the same thing. Without it a Safari user gets no recovery at all: the
    // lazy route simply never loads, and a button that navigates there looks broken.
    message.includes('Importing a module script failed') ||
    message.includes('is not a valid JavaScript MIME type') ||
    message.includes('Loading chunk') ||
    message.includes('ChunkLoadError')
  );
}

/**
 * Loop guard: allow at most MAX_RELOADS reloads within RELOAD_WINDOW_MS, so a
 * chunk that is genuinely missing in the new build (a permanent 404) cannot
 * trap the user in an infinite reload cycle.
 */
export function canReload(): boolean {
  const now = Date.now();
  const stored = sessionStorage.getItem(RELOAD_KEY);
  const reloads: number[] = stored ? (JSON.parse(stored) as number[]) : [];

  const recentReloads = reloads.filter((t) => now - t < RELOAD_WINDOW_MS);

  if (recentReloads.length >= MAX_RELOADS) {
    return false;
  }

  recentReloads.push(now);
  sessionStorage.setItem(RELOAD_KEY, JSON.stringify(recentReloads));
  return true;
}

/**
 * Hard reload with a cache-busting query param so the browser revalidates
 * index.html and fetches the current chunk names. A plain location.reload()
 * is not enough: it can be served from the browser's HTTP cache.
 */
export function hardReload(): void {
  const url = new URL(window.location.href);
  url.searchParams.set('_reload', Date.now().toString());
  window.location.replace(url.toString());
}

/**
 * If `reason` is a chunk-load failure and the loop guard permits it, trigger a
 * hard reload. Returns true when a reload was initiated, so callers can skip
 * their normal handling (e.g. error reporting) for the recovered case.
 */
export function attemptChunkReload(reason: unknown): boolean {
  if (!isChunkLoadError(reason)) {
    return false;
  }
  if (!canReload()) {
    return false;
  }
  hardReload();
  return true;
}
